import { useState } from "react";
import { Loader2, X, Package, User, Truck, MessageSquare, UserCheck } from "lucide-react";
import { updateOrderCity, updateClientCity } from "../../../../../api/auth";
import { Select } from "../../components/ui/Select";
import { StatusBadge } from "./StatusBadge";
import { ClientHistoryPanel } from "./ClientHistoryPanel";
import { ManualAssignmentPanel } from "./ManualAssignmentPanel";

const inputClass =
  "w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2.5 text-sm text-slate-900 dark:text-slate-100 outline-none transition focus:ring-2 focus:ring-indigo-500";

/* --- UI Helper: ToggleCard --- */
const ToggleCard = ({ label, description, checked, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className={`flex items-center justify-between rounded-xl border p-3 text-left transition-all ${
      checked
        ? "border-indigo-600 bg-indigo-50 dark:bg-indigo-500/10"
        : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-slate-300"
    }`}
  >
    <div className="pr-2">
      <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">{label}</div>
      <div className="mt-0.5 text-[11px] text-slate-500">{description}</div>
    </div>
    <div className={`relative h-5 w-10 shrink-0 rounded-full transition-colors ${checked ? "bg-indigo-600" : "bg-slate-200 dark:bg-slate-700"}`}>
      <span
        className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-all duration-200 ${
          checked ? "left-[22px]" : "left-0.5"
        }`}
      />
    </div>
  </button>
);

function SectionTitle({ icon: Icon, children }) {
  return (
    <h3 className="mb-3 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-400">
      <Icon className="h-3.5 w-3.5" />
      {children}
    </h3>
  );
}

const formatDateTime = (value) => {
  if (!value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleString("fr-FR", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

/* --- Main Component --- */
export function OrderModal({ order, onClose, onSaved, city = [] }) {
  const [form, setForm] = useState({
    client_name: order?.client_name || "",
    client_phone: order?.client_phone || "",
    shipping_city: order?.shipping_city || "",
    shipping_address: order?.shipping_address || "",
    total_amount: order?.total_amount ?? "",
    is_exchange: order?.is_exchange || 0,
    is_fragile: order?.is_fragile || 0,
    is_open: order?.is_open || 0,
    note: order?.note || "",
  });

  const [savingEdit, setSavingEdit] = useState(false);
  const [saveError, setSaveError] = useState("");

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleToggleChange = (field, checked) => {
    setForm((prev) => ({ ...prev, [field]: checked ? 1 : 0 }));
  };

  const validatePhone = (phone) => /^0\d{9}$/.test((phone || "").trim());
  const phoneInvalid = form.client_phone && !validatePhone(form.client_phone);

  const handleSave = async () => {
    try {
      setSavingEdit(true);
      setSaveError("");

      await updateOrderCity(order.id, {
        shipping_address: form.shipping_address,
        shipping_city: form.shipping_city,
        client_phone: form.client_phone,
        client_name: form.client_name,
        total_amount: Number(form.total_amount),
        is_exchange: form.is_exchange,
        is_fragile: form.is_fragile,
        is_open: form.is_open,
        note: form.note,
      });

      await updateClientCity(order.client, {
        full_name: form.client_name,
        phone: form.client_phone,
        address: form.shipping_address,
        city: form.shipping_city,
      });

      if (onSaved) await onSaved();
      onClose();
    } catch (error) {
      console.error("Order update failed", error);
      setSaveError(error?.response?.data?.detail || "Impossible d'enregistrer la commande.");
    } finally {
      setSavingEdit(false);
    }
  };

  const itemImage = order?.items?.[0]?.image_url;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="modal-surface flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-slate-900"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-4 dark:border-slate-800">
          <div className="min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400">
              Commande #{order.external_order_id || String(order.id).slice(0, 8)}
            </div>
            <h2 className="mt-1 truncate text-lg font-bold text-slate-900 dark:text-slate-100">
              {order.client_name || "Client"}
            </h2>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
              <StatusBadge local_status={order.local_status} shipping_status={order.shipping_status} />
              <span>{formatDateTime(order.created_at)}</span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800"
            aria-label="Fermer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-6 overflow-y-auto px-6 py-5">
          {/* Résumé produit */}
          <div className="flex items-center gap-4 rounded-xl bg-slate-50 p-3 dark:bg-slate-800">
            {itemImage ? (
              <img src={itemImage} alt="" className="h-14 w-14 rounded-lg object-cover" />
            ) : (
              <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                <Package className="h-6 w-6" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="truncate font-semibold text-slate-900 dark:text-slate-100">{order.produit || "—"}</div>
              {order.variant ? <div className="text-xs font-medium text-indigo-600">{order.variant}</div> : null}
            </div>
            <div className="text-right">
              <div className="text-lg font-bold text-slate-900 dark:text-slate-100">
                {order.total_amount} <span className="text-xs text-slate-500">{order.currency}</span>
              </div>
              {order.delivery_agency_id ? (
                <div className="text-xs text-slate-500">
                  {order.delivery_agency_id}
                  {order.delivery_tracking_id ? ` · ${order.delivery_tracking_id}` : ""}
                </div>
              ) : null}
            </div>
          </div>

          {/* Historique client (ce store uniquement) */}
          <ClientHistoryPanel orderId={order.id} />

          {/* Affectation manuelle à un agent / une agence de confirmation */}
          <section>
            <SectionTitle icon={UserCheck}>Affectation manuelle</SectionTitle>
            <ManualAssignmentPanel order={order} />
          </section>

          {/* Informations client */}
          <section>
            <SectionTitle icon={User}>Informations client</SectionTitle>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <label className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Nom du client</span>
                <input
                  type="text"
                  value={form.client_name}
                  onChange={(e) => handleChange("client_name", e.target.value)}
                  className={inputClass}
                />
              </label>

              <label className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Téléphone</span>
                <input
                  type="text"
                  value={form.client_phone}
                  onChange={(e) => handleChange("client_phone", e.target.value)}
                  className={`${inputClass} ${phoneInvalid ? "border-rose-400 bg-rose-50" : ""}`}
                />
                {phoneInvalid && (
                  <span className="text-[11px] text-rose-600">Numéro invalide (10 chiffres, commence par 0)</span>
                )}
              </label>

              <div className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Ville</span>
                <Select
                  value={form.shipping_city}
                  options={city}
                  onChange={(value) => handleChange("shipping_city", value)}
                  placeholder="Sélectionner la ville"
                />
              </div>

              <label className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Prix (MAD)</span>
                <input
                  type="number"
                  value={form.total_amount}
                  onChange={(e) => handleChange("total_amount", e.target.value)}
                  className={inputClass}
                />
              </label>

              <label className="flex flex-col gap-1.5 md:col-span-2">
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Adresse</span>
                <input
                  type="text"
                  value={form.shipping_address}
                  onChange={(e) => handleChange("shipping_address", e.target.value)}
                  className={inputClass}
                />
              </label>
            </div>
          </section>

          {/* Options de livraison */}
          <section>
            <SectionTitle icon={Truck}>Options de livraison</SectionTitle>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <ToggleCard
                label="Échange"
                description="Autoriser l'échange"
                checked={!!form.is_exchange}
                onChange={(checked) => handleToggleChange("is_exchange", checked)}
              />
              <ToggleCard
                label="Fragile"
                description="Produit fragile"
                checked={!!form.is_fragile}
                onChange={(checked) => handleToggleChange("is_fragile", checked)}
              />
              <ToggleCard
                label="Ouvrable"
                description="Le client peut ouvrir le colis"
                checked={!!form.is_open}
                onChange={(checked) => handleToggleChange("is_open", checked)}
              />
            </div>
          </section>

          {/* Note */}
          <section>
            <SectionTitle icon={MessageSquare}>Commentaire / instructions</SectionTitle>
            <textarea
              value={form.note}
              onChange={(e) => handleChange("note", e.target.value)}
              placeholder="Ajouter une instruction particulière pour le livreur…"
              className={`${inputClass} min-h-[90px]`}
            />
          </section>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 border-t border-slate-100 px-6 py-4 dark:border-slate-800">
          <p className="text-sm text-rose-600">{saveError}</p>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} disabled={savingEdit} className="btn-outline">
              Annuler
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={savingEdit || phoneInvalid}
              className="btn-primary min-w-[160px] disabled:opacity-60"
            >
              {savingEdit ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> Enregistrement…
                </span>
              ) : (
                "Enregistrer"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
